import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * ZenScoreGauge — animated radial gauge for the ZenScore preview.
 *
 * Props:
 *   value: number (0..max)
 *   max: number (default 1000)
 *   size: px diameter (default 260)
 *   label: string below score (default "out of 1000")
 *   caption: string (small note under gauge, e.g. "Strong Funding Readiness")
 *   compact: boolean — smaller typography variant for in-phone-screen use
 */
export const ZenScoreGauge = ({
  value = 782,
  max = 1000,
  size = 260,
  label = "out of 1000",
  caption,
  compact = false,
  className,
  "data-testid": testId = "zenscore-gauge",
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  // Geometry
  const strokeWidth = Math.max(8, size * 0.045);
  const radius = size / 2 - strokeWidth;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const startAngle = 135; // starts bottom-left
  const sweepAngle = 270; // 3/4 circle
  const trackDash = (sweepAngle / 360) * circumference;
  const progress = Math.min(1, Math.max(0, value / max));

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    const duration = 1600;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setDisplay(Math.round(value * ease(p)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, reduce]);

  const gradientId = React.useId();
  const glowId = React.useId();

  return (
    <div
      ref={ref}
      className={cn("relative flex flex-col items-center", className)}
      style={{ width: size }}
      data-testid={testId}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="absolute inset-0 -rotate-[225deg]"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00E676" />
              <stop offset="100%" stopColor="#00C853" />
            </linearGradient>
            <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Track */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={strokeWidth}
            strokeDasharray={`${trackDash} ${circumference}`}
            strokeLinecap="round"
          />

          {/* Progress arc */}
          <motion.circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeDasharray={`${trackDash * progress} ${circumference}`}
            strokeLinecap="round"
            filter={`url(#${glowId})`}
            initial={reduce ? { strokeDashoffset: 0 } : { strokeDashoffset: trackDash }}
            animate={inView ? { strokeDashoffset: 0 } : { strokeDashoffset: trackDash }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <span
            className={cn(
              "font-display font-semibold text-white tabular-nums leading-none tracking-tight",
              compact ? "text-5xl" : "text-6xl sm:text-7xl",
            )}
            data-testid={`${testId}-value`}
          >
            {display}
          </span>
          <span
            className={cn(
              "font-mono uppercase tracking-[0.2em] text-text-muted",
              compact ? "text-[9px]" : "text-[11px]",
            )}
          >
            {label}
          </span>
        </div>
      </div>

      {caption && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-[0_0_8px_rgba(0,230,118,0.7)]" />
          <span className="text-xs font-medium text-brand-emerald">{caption}</span>
        </div>
      )}
    </div>
  );
};

export default ZenScoreGauge;
